import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DateTimeV2, { DATETIME_VIEW } from "../../../components/datetime-v2/datetime-v2";
import { useDateTimeFormattingParams } from "../../../hooks/use-datetime-formatting-params";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import FontBody2 from "../../../components/fonts/font-body2/font-body2";
import { FONT_COLOR, FONT_VARIANT } from "../../../components/fonts/font-props";
import IconFunnel from "../../../components/icons/icon-funnel/icon-funnel";
import IconGift from "../../../components/icons/icon-gift/icon-gift";
import "./rma-list-page.scss";
import IconSirenOnLight from "../../../components/icons/icon-siren-on-light/icon-siren-on-light";
import Icon from "../../../components/icons/icon/icon";
import { BUTTON_VARIANTS } from "../../../components/inputs/button-variants";
import { Button } from "../../../components/inputs/button/button";
import { Toggle } from "../../../components/inputs/toggle/toggle";
import usePage from "../../../components/layouts/base-page-layout/page-hook";
import Card from "../../../components/layouts/card/card";
import SearchBar from "../../../components/layouts/search-bar/search-bar";
import SearchInput from "../../../components/search-input/search-input";
import TableRow from "../../../components/table/components/table-row/table-row";
import Table from "../../../components/table/table";
import { Tooltip } from "../../../components/tooltip/tooltip";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import dashboardTranslationsStore from "../../../dashboard-translation/stores/dashboard-translations-store";
import uiSettingsStore from "../../../stores/ui-settings-store";
import ExternalLink from "../components/external-link/external-link";
import RmaStatusChip from "../components/rma-status-chip/rma-status-chip";
import RmaRequireAttentionIcon from "../rma-page/components/rma-require-attention-icon/rma-require-attention-icon";
import RmaImages from "./components/rma-images/rma-images";
import RmaItems from "./components/rma-items/rma-items";
import RmaListFiltersDrawer from "./components/rma-list-filters-drawer/rma-list-filters-drawer";
import RmaListSettingsButton from "./components/rma-list-settings-button/rma-list-settings-button";
import RmaListStatusesFiltersTabs from "./components/rma-list-statuses-filters-tabs/rma-list-statuses-filters-tabs";
import RmaReportButton from "./components/rma-report-button/rma-report-button";
import RmaResolutionName from "./components/rma-resolution-name/rma-resolution-name";
import RmaListItemStore from "./stores/rma-item-store";
import { IFiltersButtonstyle } from "./stores/rma-list-filters-store";
import rmaPagedListStore from "./stores/rma-paged-list-store";
import RmaTableDateColumnsTitle from "./components/rma-table-date-column-title/rma-table-date-column-title";
import rmsMixpanelStore from "../../../stores/rms-mixpanel-store";
import showSbrPageStore from "./stores/show-sbr-page.store";
import CreateRmaListAction from "../components/create-rma-list-action/create-rma-list-action";


export const RmaListPage: React.FC = observer(() => {
    /** TODO Arthur: const [, seSearchParams] = useSearchParams(); */
    const rmaListFiltersStore = rmaPagedListStore.rmaListFiltersStore;
    const dateTimeParams = useDateTimeFormattingParams();

    const navigate = useNavigate();
    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-page';
    const isLoading = rmaPagedListStore.isLoading ||
        dashboardTranslationsStore.isLoading ||
        rmaListFiltersStore.isLoading ||
        uiSettingsStore.isLoading ||
        uiSettingsStore.isSaving();

    const translatedSubtitle = getTranslation('MANAGE_RETURNS_SUB_TITLE', scope);
    const { setInstruction, setSubTitles, setHasBack } = usePage();

    useEffect(() => {
        /* in case if we go back from single rma page*/
        setSubTitles(undefined);
        setHasBack(false);
    }, [setSubTitles, setHasBack]);

    useEffect(() => {
        setInstruction(translatedSubtitle);
    }, [translatedSubtitle, setInstruction]);

    useEffect(() => {
        rmaPagedListStore.load();
        rmsMixpanelStore.load();
        showSbrPageStore.fetchShowSBRPageOnce();
    }, []);

    useEffect(() => {
        rmaListFiltersStore.loadFilterOptions();
    }, [rmaListFiltersStore]);

    const onSearchClick = useCallback(() => {
        rmsMixpanelStore.trackEvent('d&_search_rma_input', { input: rmaListFiltersStore.searchInput });
        rmaPagedListStore.loadPage(1);
        /** TODO Arthur: seSearchParams(rmaListFiltersStore.searchURLParams); */
    }, [rmaListFiltersStore]);

    const onSearchInputChange = useCallback((value: string) => {
        rmaListFiltersStore.setSearchCustomerId(value);
    }, [rmaListFiltersStore]);

    const onSearchExactMatchChange = useCallback(() => {
        rmaListFiltersStore.toggleSearchExactMatch();
        if (rmaListFiltersStore.searchInput) {
            rmaPagedListStore.loadPage(1);
        }
    }, [rmaListFiltersStore]);

    const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);

    const onPaginationSetPage = async (page: number) => {
        rmaPagedListStore.resetIsLoaded();
        window.scrollTo(0, 0);
        await rmaPagedListStore.loadPage(page);
    }

    const onSortPagedList = (by: string, dir: string) => {
        if (by === 'date_to_show') {
            rmsMixpanelStore.trackEvent('d&_date_column');
        }
        rmaPagedListStore.resetIsLoaded();
        rmaPagedListStore.sortData(by, dir);
    }

    const filtersButtonStyle= ():IFiltersButtonstyle => {
        if (rmaListFiltersStore.hasActiveDrawerFilters()) {
            return { variant: BUTTON_VARIANTS.PrimaryAccent, color: 'var(--color-neutral-1-white)'};
        }
        return {variant: BUTTON_VARIANTS.Secondary, color: 'var(--color-accent-1)'};
    }

    const onOrderNumberClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation(); /* Prevent opening RMA page */
        rmsMixpanelStore.trackEvent('d&_order');
    }

    const onCustomerIDClick = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation(); /* Prevent opening RMA page */
        rmsMixpanelStore.trackEvent('d&_customer');
    }

    const onFiltersChange = () => { 
        /** TODO: stub method to update url query by applied filter */
        /** TODO Arthur: seSearchParams(rmaListFiltersStore.searchURLParams); */
    }

    return (
        <>
            <Card>
                <SearchBar>
                    <SearchInput
                        placeholder={getTranslation('SEARCH_FIELD_PLACEHOLDER', scope)}
                        value={rmaListFiltersStore.searchInput ?? ''}
                        onClick={onSearchClick}
                        onChange={onSearchInputChange}
                    />
                    <Toggle
                        disabled={isLoading}
                        checked={rmaListFiltersStore.searchExactMatch}
                        label={getTranslation('EXACT_MATCH', scope)}
                        onChange={onSearchExactMatchChange}
                    />
                    {!dashboardTranslationsStore.isLoading && <Button
                        variant={filtersButtonStyle().variant}
                        leftIcon={() => <IconFunnel color={filtersButtonStyle().color} />}
                        text={getTranslation('FILTERS_TITLE', scope)}
                        disabled={!rmaListFiltersStore.areFilterOptionsLoaded}
                        onClick={() => {
                            rmsMixpanelStore.trackEvent('d&_filters_button');
                            setShowFiltersDrawer(true);
                        }}
                    />}
                    {!dashboardTranslationsStore.isLoading && <RmaListSettingsButton />}
                    <div className="rma-search-bar-side-content">
                        {(!dashboardTranslationsStore.isLoading) && (
                            <CreateRmaListAction getCreateRmaLabel={() => getTranslation('CREATE_RMA', scope)} />
                        )}
                        {!dashboardTranslationsStore.isLoading && <RmaReportButton
                            getReportFilters={rmaPagedListStore.rmaListFiltersStore.getReportFilters}
                        />}
                    </div>

                </SearchBar>
                {showFiltersDrawer && <RmaListFiltersDrawer
                    onClose={() => setShowFiltersDrawer(false)}
                    rmaListFiltersStore={rmaPagedListStore.rmaListFiltersStore}
                />}
                {!dashboardTranslationsStore.isLoading && <RmaListStatusesFiltersTabs
                    disabled={isLoading}
                    rmaListFiltersStore={rmaPagedListStore.rmaListFiltersStore}
                    onFilterChange={onFiltersChange}
                />}
                <div className="rma-table">
                    <Table
                        isLoading={isLoading}
                        totalResultsTitle='returns'
                        pagination={{
                            onSetPage: onPaginationSetPage,
                            page: rmaPagedListStore.page,
                            total: rmaPagedListStore.total,
                            perPage: rmaPagedListStore.perPage,
                        }}
                        sort={{
                            onSort: onSortPagedList,
                            by: rmaPagedListStore.sortBy,
                            dir: rmaPagedListStore.sortDir,
                        }}
                        columns={[
                            { content: getTranslation('RMA#', scope), width: { value: 180, type: 'px' } },
                            { content: getTranslation('ORDER', scope), width: { value: 1, type: 'fr' } },
                            { content: <RmaTableDateColumnsTitle />, width: { value: 1, type: 'fr' }, sortable: true, sortBy: 'date_to_show' },
                            { content: getTranslation('RESOLUTIONS', scope), width: { value: 2, type: 'fr' } },
                            { content: getTranslation('IMAGE', scope), width: { value: 60, type: 'px' } },
                            { content: getTranslation('ITEMS', scope), width: { value: .5, type: 'fr' } },
                            { content: getTranslation('CUSTOMER', scope), width: { value: 1, type: 'fr' } },
                            { content: getTranslation('STATUS', scope), width: { value: 1.5, type: 'fr' } },
                        ]}
                    >
                        {rmaPagedListStore.list.map((rma: RmaListItemStore) => {
                            return (
                                <TableRow key={rma.rmaId} cells={[
                                    <div className="rma-id-container">
                                        <div className="rma-indicators-container">
                                            {rma.lastUnresolvedError && (
                                                <RmaRequireAttentionIcon
                                                    errorText={rma.lastUnresolvedError}
                                                />
                                            )}
                                        </div>
                                        <FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>{getTranslation('RMA', scope)}{rma.rmaId}</FontBody1>
                                        {rma.isGift && <div className="rma-indicators-container"><IconGift /></div>}
                                        {rma.isOutOfPolicy && <div className="rma-indicators-container">
                                            <Tooltip
                                                content={
                                                    <FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                                                        {getTranslation('RMA_OUT_OF_POLICY_INDICATOR_TEXT', scope)}
                                                    </FontBody2>
                                                }
                                            >
                                                <Icon Component={IconSirenOnLight} />
                                            </Tooltip>
                                        </div>}
                                    </div>,
                                    <div className="rma-order-name-cell" onClick={(e) => onOrderNumberClick(e)}>
                                        <ExternalLink label={rma.orderName} url={rma.orderUrl} Font={FontBody2} />
                                    </div>,
                                    <DateTimeV2 {...dateTimeParams} utcDateString={rma.dateString} dateTimeView={DATETIME_VIEW.DATE_ONLY}/>,
                                    <RmaResolutionName rma={rma} />,
                                    <div className="rma-product-media-container">
                                        <RmaImages imagesUrls={rma.imagesUrls} />
                                    </div>,
                                    <RmaItems rma={rma} />,
                                    <div onClick={(e) => onCustomerIDClick(e)}>
                                        <ExternalLink label={rma.customerName} url={rma.customerUrl} Font={FontBody2} />
                                    </div>,
                                    <RmaStatusChip statusKey={rma.statusTranslationKey} />,
                                ]}
                                    onSelect={() => {
                                        rmsMixpanelStore.trackEvent('d&_table_row');

                                        navigate({
                                            pathname: '/manage-returns/rma-list-page/' + rma.rmaId
                                        });
                                    }} />
                            )
                        })}
                    </Table>
                </div>
            </Card>
        </>
    );
});

export default RmaListPage;